import { createContext, FC, PropsWithChildren, useContext } from 'react';
import { CustomForm, FpCompany } from '@flicent/fieldpulse-react-component-library-frontend';

import { CUSTOM_FORM_OBJECT_TYPE_TO_ANALYTICS_RECORD_TYPE } from '@/constants';
import { PendoAnalyticsPlatform, usePendoAnalytics } from '@/hooks';


export interface TrackFormSubmittedMetadata {
  isFormAttachedToRecord: boolean;
  form: CustomForm;
  company: FpCompany;
}

export interface TrackFormSignedMetadata {
  signerName: string | null;
  form: CustomForm;
  company: FpCompany;
}

interface CustomFormAnalyticsPublicLinkContextValue {
  trackFormSubmitted: (metadata: TrackFormSubmittedMetadata) => void;
  trackFormSigned: (metadata: TrackFormSignedMetadata) => void;
}

const CustomFormAnalyticsPublicLinkContext = createContext<CustomFormAnalyticsPublicLinkContextValue | null>(null);

export const useCustomFormAnalyticsPublicLinkContext = (): CustomFormAnalyticsPublicLinkContextValue => {
  const value = useContext(CustomFormAnalyticsPublicLinkContext);

  if (!value) {
    throw new Error('CustomFormAnalyticsPublicLinkContextProvider is missing');
  }

  return value;
};

export const CustomFormAnalyticsPublicLinkContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { trackEvent } = usePendoAnalytics();

  const platform = PendoAnalyticsPlatform.Web; // Using 'web' as the public Link is always a browser web page

  const getInitOptions = (company: FpCompany) => ({
    account: { id: company.id.toString() },
    visitor: { id: 'public_link' }
  });

  const trackFormSubmitted = ({ isFormAttachedToRecord, form, company }: TrackFormSubmittedMetadata): void => {
    void trackEvent(getInitOptions(company), 'Form Submitted', {
      form_id: form.id,
      form_template_id: form.custom_form_template_id,
      record_type: isFormAttachedToRecord ? CUSTOM_FORM_OBJECT_TYPE_TO_ANALYTICS_RECORD_TYPE[form.object_type] : null,
      record_id: isFormAttachedToRecord ? form.object_id : null,
      platform,
    });
  };

  const trackFormSigned = ({ signerName, form, company }: TrackFormSignedMetadata): void => {
    void trackEvent(getInitOptions(company), 'Form Signed', { form_id: form.id, signer: signerName, platform, });
  };

  return (
    <CustomFormAnalyticsPublicLinkContext.Provider value={ { trackFormSubmitted, trackFormSigned } }>
      { children }
    </CustomFormAnalyticsPublicLinkContext.Provider>
  );
};